export function normalizeFormConfig(raw) {
	const data = raw?.data ?? raw ?? {};
	return {
		tab_fields: data.tab_fields && typeof data.tab_fields === 'object' ? data.tab_fields : {},
		description: data.description || '',
	};
}

export function getTabFieldMap(config, tab) {
	const tabData = config?.tab_fields?.[tab];
	if (!tabData) return null;
	if (tabData.fields && typeof tabData.fields === 'object' && !Array.isArray(tabData.fields)) {
		return tabData.fields;
	}
	return tabData;
}

export function getFieldConfig(config, tab, field) {
	const fields = getTabFieldMap(config, tab);
	return fields?.[field] ?? null;
}

// ---- visible / enabled --------------------------------------------------

function resolveBoolean(value, ctx, defaultValue) {
	if (value === undefined || value === null) return defaultValue;
	if (typeof value === 'boolean') return value;
	return evaluateCondition(value, ctx);
}

export function isFieldVisible(config, tab, field, ctx) {
	if (!config?.tab_fields) return true;
	const fc = getFieldConfig(config, tab, field);
	if (!fc) return false;
	return resolveBoolean(fc.visible, ctx, true);
}

// Tanpa `ctx` (dipanggil di titik yang belum punya form state, mis. resolve() BE): cek statis --
// tab dianggap visible kalau ADA field yang tidak di-hardcode `visible: false`.
// Dengan `ctx`: dievaluasi dinamis -- tab visible kalau ADA field yang BENAR-BENAR visible
// saat ini (termasuk field yang visible-nya condition object, mis. gantung ke mcr_type),
// bukan cuma field yang lolos cek "bukan literal false".
export function isTabVisible(config, tab, ctx) {
	if (!config?.tab_fields) return true;
	const fields = getTabFieldMap(config, tab);
	if (!fields) return false;

	if (!ctx) {
		return Object.values(fields).some(f => f.visible !== false);
	}

	return Object.entries(fields).some(([field, fc]) => {
		// Field bertingkat (mis. Manufacture -> emcr_manufacturers): field-nya sendiri sering tidak
		// punya `visible` (cuma di tiap column), jadi visibility-nya ditentukan dari kolom-kolomnya.
		if (fc.columns) {
			return hasVisibleColumn(config, tab, field, ctx);
		}
		return isFieldVisible(config, tab, field, ctx);
	});
}

// Field tabel (punya `columns`) dianggap tampil kalau ADA kolom yang visible untuk ctx saat ini. Satu sumber untuk
// isTabVisible (di atas) & tabel per card di tab FORM02 (isTableVisible di New_CataloguerTab.js).
export function hasVisibleColumn(config, tab, field, ctx) {
	const columns = getFieldConfig(config, tab, field)?.columns || {};
	return Object.keys(columns).some(col => isColumnVisible(config, tab, field, col, ctx));
}

export function isFieldEnabled(config, tab, field, ctx) {
	if (ctx?.isView) return false; // untuk set readonly secara global (all field)
	const fc = getFieldConfig(config, tab, field);
	if (!fc) return true;
	return resolveBoolean(fc.enabled, ctx, true);
}

// ---- kolom di field repeatable (mis. Manufacture -> emcr_manufacturers.columns.manufacture) --------

export function getColumnConfig(config, tab, field, column) {
	const fc = getFieldConfig(config, tab, field);
	return fc?.columns?.[column] ?? null;
}

export function isColumnVisible(config, tab, field, column, ctx) {
	if (!config?.tab_fields) return true;
	const col = getColumnConfig(config, tab, field, column);
	if (!col) return false;
	return resolveBoolean(col.visible, ctx, true);
}

export function isColumnEnabled(config, tab, field, column, ctx) {
	if (ctx?.isView) return false;
	const col = getColumnConfig(config, tab, field, column);
	if (!col) return true;
	return resolveBoolean(col.enabled, ctx, true);
}

export function isColumnMandatory(config, tab, field, column, ctx) {
	const col = getColumnConfig(config, tab, field, column);
	if (!col) return false;
	if (!isColumnEnabled(config, tab, field, column, ctx)) return false;

	const requiredRule = (col.rules || []).find(r => r.type === 'required');
	if (!requiredRule) return false;
	return !requiredRule.when || evaluateCondition(requiredRule.when, ctx);
}

// Boleh tambah (`action` "add") / hapus ("remove") baris field tabel: `row_actions.<action>` boolean atau condition,
// default true. Murni FE (BE tidak membaca, sama seperti rows_from/hydrate_from). Mode lihat-saja (isView) TIDAK
// dicek di sini -- tetap urusan prop `disabled` komponen. Lihat FORM_CONFIG_SCHEMA.md "row_actions".
// `defaultValue` untuk aksi yang default-nya TIDAK boleh (mis. "duplicate" -- tambah baris tepat di bawah satu baris,
// dievaluasi dengan ctx BARIS itu). Tanpa argumen ini perilakunya sama persis dengan dulu.
export function isRowActionAllowed(config, tab, field, action, ctx, defaultValue = true) {
	const fc = getFieldConfig(config, tab, field);
	return resolveBoolean(fc?.row_actions?.[action], ctx, defaultValue);
}

// Aturan unik antar baris (`row_checks` type "unique") yang memuat `column`: tiap entri = kolom PASANGAN-nya di aturan itu.
// [] = kolom ini sendiri unik; ["plant", "warehouse_number"] = unik di antara baris yang plant & warehouse_number-nya sama.
// Contoh FORM02 Warehouse: INALUM `warehouse_number` unik; ANTAM Storage Type unik per plant + warehouse_number.
export function getUniqueColumnPeers(config, tab, field, column) {
	const fc = getFieldConfig(config, tab, field);
	return (fc?.row_checks || [])
		.filter((check) => check?.type === 'unique' && Array.isArray(check.fields) && check.fields.includes(column))
		.map((check) => check.fields.filter((name) => name !== column));
}

// ---- rules (required) ----------------------------------------------------

export function isFieldMandatory(config, tab, field, ctx) {
	const fc = getFieldConfig(config, tab, field);
	if (!fc) return false;
	if (!isFieldEnabled(config, tab, field, ctx)) return false;

	const requiredRule = (fc.rules || []).find(r => r.type === 'required');
	if (!requiredRule) return false;
	return !requiredRule.when || evaluateCondition(requiredRule.when, ctx);
}

export function isSectionMandatory(config, tab, ctx) {
	const fields = getTabFieldMap(config, tab);
	if (!fields) return false;

	return Object.values(fields).some(fc => {
		// Field bertingkat (Manufacture, dst): cek tiap kolom, karena
		// kolom tidak terdaftar sebagai field_code langsung di tab_fields
		// (nested di dalam .columns), jadi isFieldMandatory(tab, field)
		// biasa tidak bisa nemuin mereka lewat lookup nama.
		if (fc.columns) {
			return Object.values(fc.columns).some(col => {
				if (!resolveBoolean(col.enabled, ctx, true)) return false;
				const req = (col.rules || []).find(r => r.type === 'required');
				return !!req && (!req.when || evaluateCondition(req.when, ctx));
			});
		}

		// Field repeatable tanpa columns (mis. Attachment): min_rows > 0
		// atau row_rules eksplisit "required" dianggap mandatory.
		if (fc.row_rules) {
			return countRequiredRows(fc.row_rules, ctx) > 0;
		}

		if (!resolveBoolean(fc.enabled, ctx, true)) return false;
		const req = (fc.rules || []).find(r => r.type === 'required');
		return !!req && (!req.when || evaluateCondition(req.when, ctx));
	});
}

// Ambil `value` dari satu row_rule type tertentu (mis. "mimes" -> list ekstensi),
// dipakai field repeatable tanpa columns (mis. Attachment) buat baca aturan yang
// sifatnya bukan visible/enabled/mandatory biasa. Return undefined kalau rule-nya
// tidak ada, biar caller bisa fallback sendiri.
export function getRowRuleValue(config, tab, field, type) {
	const fc = getFieldConfig(config, tab, field);
	const rule = (fc?.row_rules || []).find(r => r.type === type);
	return rule ? rule.value : undefined;
}

// Jumlah baris minimal field repeatable dari `row_rules`: max(min_rows, 1 kalau ada "required"). Satu sumber untuk
// tanda * (isSectionMandatory, isTableMandatory tab FORM02) & validasi Save (New_McrFooter.validateSingleMcrItem).
// Rule ber-`when` (mis. Accounting FORM02 wajib cuma kalau ada plant di luar daftar tertentu) cuma dihitung kalau `when`-nya
// cocok -- tanpa ctx dianggap tidak berlaku. Mirror FormConfigRuleBuilder::buildTableRules di BE.
function countRequiredRows(rowRules, ctx) {
	const rules = (Array.isArray(rowRules) ? rowRules : []).filter((r) => !r.when || (ctx && evaluateCondition(r.when, ctx)));
	const minRows = Number(rules.find(r => r.type === 'min_rows')?.value) || 0;
	const required = rules.some(r => r.type === 'required');
	return Math.max(minRows, required ? 1 : 0);
}

export function getRequiredRowCount(config, tab, field, ctx) {
	return countRequiredRows(getFieldConfig(config, tab, field)?.row_rules, ctx);
}

// ---- readonlyandsaved ------------------------------------------------------
// Field/kolom tampil TERKUNCI tapi nilainya tetap dikirim & disimpan (beda dengan `enabled: false` yang dikosongkan BE).
// Murni FE: BE melihatnya sebagai field biasa yang enabled. Boolean atau condition, default false.
export function isReadonlySaved(config, tab, field, ctx) {
	return resolveBoolean(getFieldConfig(config, tab, field)?.readonlyandsaved, ctx, false);
}

export function isColumnReadonlySaved(config, tab, field, column, ctx) {
	return resolveBoolean(getColumnConfig(config, tab, field, column)?.readonlyandsaved, ctx, false);
}

// ---- multiple -------------------------------------------------------------
// Field boleh pilih LEBIH DARI SATU nilai (mis. plant untuk material type ZFGD). Boolean atau condition,
// default false (satu pilihan). Murni FE: nilainya dikirim sebagai array (`plant[i]`), sedangkan BE
// menerima array maupun scalar -- jadi tidak ada padanan di FormConfigEvaluator.
export function isFieldMultiple(config, tab, field, ctx) {
	return resolveBoolean(getFieldConfig(config, tab, field)?.multiple, ctx, false);
}

// Field/kolom `readonlyandsaved` yang punya `default_value` static: nilainya SELALU nilai default yang cocok saat ini
// (user tidak bisa mengubahnya), mis. Authorization Group INALUM "ZSD1", Qty Struct per Material Type. Dihitung ulang tiap
// tab dibuka / ada perubahan / sebelum Save, langsung ke objek item (satu objek yang dipakai semua tab & payload).
// Baris tabel yang selain kolom terkunci masih kosong tidak diisi (kolom terkuncinya dikosongkan) -- supaya baris kosong
// (mis. baris Accounting otomatis) tetap dianggap kosong & tidak ikut payload.
export function applyLockedDefaults(config, item, ctx) {
	if (!config || !item) return;
	Object.keys(config.tab_fields || {}).forEach((tab) => {
		Object.entries(getTabFieldMap(config, tab) || {}).forEach(([field, fc]) => {
			if (!fc || typeof fc !== 'object') return;
			if (fc.columns) {
				const rows = Array.isArray(item[field]) ? item[field] : [];
				const sourceOwned = rowsFromSourceColumns(config, fc);
				// Tabel `rows_from` memakai ARRAY sumber -> baris tidak dibuang dari sini (row_filter milik tabel sumber).
				if (!fc.rows_from) applyRowFilter(fc, rows, ctx);
				clearDisabledColumns(config, tab, field, fc, rows, ctx, sourceOwned);
				const lockedColumns = Object.keys(fc.columns).filter((column) => !sourceOwned.has(column)
					&& fc.columns[column]?.readonlyandsaved && fc.columns[column]?.default_value);
				if (!lockedColumns.length) return;
				const otherColumns = Object.keys(fc.columns).filter((column) => !lockedColumns.includes(column));
				rows.forEach((row) => {
					if (!row || typeof row !== 'object') return;
					const rowCtx = withRowCtx(ctx, row);
					const hasData = otherColumns.some((column) => isFilledValue(row[column]));
					lockedColumns.forEach((column) => {
						if (!isColumnVisible(config, tab, field, column, rowCtx) || !isColumnReadonlySaved(config, tab, field, column, rowCtx)) return;
						if (!hasData) { if (isFilledValue(row[column])) row[column] = ''; return; }
						const entry = resolveActiveColumnDefault(config, tab, field, column, rowCtx);
						if (entry?.type === 'static') row[column] = entry.value;
						// relative_date (mis. Valid Until = hari ini saat Type Z1/Z2): cuma diisi kalau masih kosong -- tanggal yang
						// sudah tersimpan tidak bergeser tiap item dibuka. Diisi ulang saat kolom pemicunya diganti (onColumnChange).
						else if (entry?.type === 'relative_date' && !isFilledValue(row[column])) row[column] = resolveSimpleDefault(entry);
					});
				});
				return;
			}
			if (!fc.readonlyandsaved || !fc.default_value) return;
			if (!isFieldVisible(config, tab, field, ctx) || !isReadonlySaved(config, tab, field, ctx)) return;
			const entry = resolveActiveDefault(config, tab, field, ctx);
			if (entry?.type === 'static') item[field] = entry.value;
			else if (entry?.type === 'relative_date' && !isFilledValue(item[field])) item[field] = resolveSimpleDefault(entry);
		});
	});
	applyValuePrefixes(config, item, ctx);
}

// Pengaman tabel `rows_from` (baris = OBJEK YANG SAMA dengan baris tabel sumber, mis. MRP / General Plant Data = baris Plant):
// kolom yang juga ada di tabel SUMBER (mis. `plant`) milik tabel sumber -- aturan FE yang menulis ke baris (kosongkan kolom
// disabled, nilai terkunci, awalan nilai) TIDAK boleh mengubahnya lewat tabel turunan, apa pun visible/enabled kolom itu di
// tabel turunan. Kolom itu cuma diatur dari tabel sumbernya. Field tanpa `rows_from` -> Set kosong (perilaku tidak berubah).
function rowsFromSourceColumns(config, fc) {
	if (!fc?.rows_from) return new Set();
	for (const tab of Object.keys(config?.tab_fields || {})) {
		const source = getFieldConfig(config, tab, fc.rows_from);
		if (source?.columns) return new Set(Object.keys(source.columns));
	}
	return new Set();
}

// `row_filter` field tabel (condition ctx baris): baris yang tidak cocok dibuang DI TEMPAT (array yang sama -- dipakai
// bersama tab & payload), mis. FORM02 INALUM Accounting: tidak ada baris Valuation Area Plant NV (A0N1-A0N4). Baris yang
// kolomnya masih kosong tetap ada (kondisi `not_in` cocok untuk nilai kosong). Mirror BE FormConfigGuard::sanitizeTableRows.
function applyRowFilter(fc, rows, ctx) {
	if (!fc?.row_filter || !Array.isArray(rows)) return;
	for (let i = rows.length - 1; i >= 0; i -= 1) {
		const row = rows[i];
		if (row && typeof row === 'object' && !evaluateCondition(fc.row_filter, withRowCtx(ctx, row))) rows.splice(i, 1);
	}
}

// Kolom tabel yang di baris itu tampil tapi TIDAK enabled (dan tidak `readonlyandsaved`) dikosongkan -- sama dengan yang
// dilakukan BE (FormConfigGuard: nilainya null saat Save), supaya sel disabled tidak menampilkan nilai yang tidak akan
// tersimpan (mis. default GR Processing Time baris baru, lalu plant diganti ke Plant NV).
function clearDisabledColumns(config, tab, field, fc, rows, ctx, sourceOwned = new Set()) {
	const columns = Object.keys(fc.columns || {}).filter((column) => !sourceOwned.has(column) && fc.columns[column]?.enabled !== undefined);
	if (!columns.length) return;
	rows.forEach((row) => {
		if (!row || typeof row !== 'object') return;
		const rowCtx = withRowCtx(ctx, row);
		columns.forEach((column) => {
			if (!isFilledValue(row[column])) return;
			// Kolom TIDAK tampil di tabel ini dilewati: barisnya bisa objek yang sama dengan tabel lain (`rows_from`), mis. ANTAM
			// `cat_emcr_sales_general.plant` visible false (BE memberi enabled false) -- tanpa ini plant baris Plant ikut terhapus.
			if (!isColumnVisible(config, tab, field, column, rowCtx)) return;
			if (isColumnEnabled(config, tab, field, column, rowCtx) || isColumnReadonlySaved(config, tab, field, column, rowCtx)) return;
			row[column] = Array.isArray(row[column]) ? [] : '';
		});
	});
}

// `value_prefix` kolom tabel: `{ value, when? }` -- kondisi cocok -> nilai (yang terisi) diawali `value`, tidak cocok -> awalan
// itu dibuang (mis. Manufacture Ref & Ref Description INALUM: "(S)" kalau Type Z2). Murni FE, dipanggil applyLockedDefaults
// (tab: tiap perubahan / blur; Save: sebelum validasi & payload). Lihat FORM_CONFIG_SCHEMA.md "value_prefix".
export function applyValuePrefixes(config, item, ctx) {
	if (!config || !item) return;
	Object.keys(config.tab_fields || {}).forEach((tab) => {
		Object.entries(getTabFieldMap(config, tab) || {}).forEach(([field, fc]) => {
			if (!fc?.columns) return;
			const sourceOwned = rowsFromSourceColumns(config, fc);
			const prefixed = Object.keys(fc.columns).filter((column) => !sourceOwned.has(column) && fc.columns[column]?.value_prefix?.value);
			if (!prefixed.length) return;
			(Array.isArray(item[field]) ? item[field] : []).forEach((row) => {
				if (!row || typeof row !== 'object') return;
				const rowCtx = withRowCtx(ctx, row);
				prefixed.forEach((column) => {
					const { value: prefix, when } = fc.columns[column].value_prefix;
					const current = row[column];
					if (typeof current !== 'string' || current === '') return;
					const active = !when || evaluateCondition(when, rowCtx);
					if (active && !current.startsWith(prefix)) row[column] = prefix + current;
					else if (!active && current.startsWith(prefix)) row[column] = current.slice(prefix.length);
				});
			});
		});
	});
}

// Nilai default yang bisa dihitung tanpa fetch: static (`value`) & relative_date (`days_from_now` dari hari ini, "YYYY-MM-DD").
// Tipe lain -> undefined. Dipakai addRow/onColumnChange tab FORM02 & applyLockedDefaults.
export function resolveSimpleDefault(entry) {
	if (entry?.type === 'static') return entry.value;
	if (entry?.type === 'relative_date') {
		const date = new Date();
		date.setDate(date.getDate() + (Number(entry.days_from_now) || 0));
		return formatDate(date);
	}
	return undefined;
}

// Nama field yang dirujuk sebuah kondisi (leaf `field`, grup `conditions`, `has_line` + `where`). Dipakai untuk tahu kolom
// mana yang default-nya harus dihitung ulang saat kolom lain diubah.
export function getConditionFields(condition) {
	if (!condition || typeof condition !== 'object') return [];
	if (Array.isArray(condition.conditions)) return condition.conditions.flatMap(getConditionFields);
	if (condition.operator === 'has_line') return getConditionFields(condition.where);
	return condition.field ? [condition.field] : [];
}

// ---- default_value --------------------------------------------------------

// Entry pertama yang `when`-nya cocok (entry tanpa `when` = fallback, taruh PALING AKHIR).
function firstMatchingEntry(entries, ctx) {
	for (const entry of entries || []) {
		if (!entry.when || evaluateCondition(entry.when, ctx)) return entry;
	}
	return undefined;
}

export function resolveActiveDefault(config, tab, field, ctx) {
	return firstMatchingEntry(getFieldConfig(config, tab, field)?.default_value, ctx);
}

// Versi kolom field repeatable -- `ctx` biasanya ctx per baris (withRowCtx). Dipakai addRow tab FORM02.
export function resolveActiveColumnDefault(config, tab, field, column, ctx) {
	return firstMatchingEntry(getColumnConfig(config, tab, field, column)?.default_value, ctx);
}

// ---- hydrate_from ------------------------------------------------------------

// Entry `hydrate_from` yang aktif (first-match-wins, entry tanpa `when` = fallback, taruh paling akhir) --
// nentuin key response mana yang mengisi field saat item dibuka. Biasanya `when` membaca context `item_source`.
// undefined -> field dibaca dari key response yang namanya sama. Lihat FORM_CONFIG_SCHEMA.md "hydrate_from".
export function resolveActiveHydrate(config, tab, field, ctx) {
	return firstMatchingEntry(getFieldConfig(config, tab, field)?.hydrate_from, ctx);
}

// ---- depends_on / data_source -----------------------------------------------

// `depends_on` field biasa boleh 2 bentuk:
// - array nama field (bentuk lama, dipakai semua form): ["material_type"];
// - array entry BERSYARAT first-match-wins (pola sama dengan default_value/hydrate_from), entry tanpa `when` =
//   fallback, taruh PALING AKHIR: [{ "when": {...}, "fields": ["cat_inc"] }, { "fields": [] }].
// Bentuk ke-2 cuma dikenali kalau elemen pertamanya object -- array string tetap dibaca persis seperti dulu.
export function resolveDependsOn(dependsOn, ctx) {
	if (!Array.isArray(dependsOn)) return [];
	if (!dependsOn.length || typeof dependsOn[0] !== 'object' || dependsOn[0] === null) return dependsOn;
	const entry = firstMatchingEntry(dependsOn, ctx || {});
	return Array.isArray(entry?.fields) ? entry.fields : [];
}

export function getFieldDependsOn(config, tab, field, ctx) {
	return resolveDependsOn(getFieldConfig(config, tab, field)?.depends_on, ctx);
}

export function getFieldDataSource(config, tab, field) {
	const fc = getFieldConfig(config, tab, field);
	return fc?.data_source ?? null;
}

// `auto_select_single` (boolean | condition, default false): kalau opsi field ini cuma SATU, langsung dipilih
// (mis. Material Type INALUM MCR001 dari Item Name). Murni FE.
export function isAutoSelectSingle(config, tab, field, ctx) {
	const fc = getFieldConfig(config, tab, field);
	return resolveBoolean(fc?.auto_select_single, ctx, false);
}

// `option_values` (field biasa, murni FE): batasi opsi select ke daftar nilai, first-match-wins
// `[{ when?, values: [...] }]` (mis. Plant INALUM: A001-A004/A0N1-A0N4, ZFGD tanpa A002/A0N2). Tidak ada entry yang
// cocok / field tanpa key ini -> null (semua opsi boleh, perilaku lama).
// Entry boleh juga `exclude: [...]` (nilai yang TIDAK boleh muncul, sisanya boleh), mis. Valuation Area FORM02 INALUM tanpa
// Plant NV. `column` (opsional): `option_values` di config KOLOM tabel, `ctx` = ctx baris.
export function resolveOptionRule(config, tab, field, ctx, column) {
	const fc = column ? getColumnConfig(config, tab, field, column) : getFieldConfig(config, tab, field);
	const entry = firstMatchingEntry(fc?.option_values, ctx);
	if (!entry) return null;
	return {
		values: Array.isArray(entry.values) ? entry.values.map(String) : null,
		exclude: Array.isArray(entry.exclude) ? entry.exclude.map(String) : [],
	};
}

export function isOptionValueAllowed(rule, value) {
	if (!rule) return true;
	const v = String(value ?? '');
	if (rule.values && !rule.values.includes(v)) return false;
	return !rule.exclude.includes(v);
}

export function resolveOptionValues(config, tab, field, ctx) {
	return resolveOptionRule(config, tab, field, ctx)?.values ?? null;
}

export function hasConditionalOptionValues(config, tab, field, column) {
	const fc = column ? getColumnConfig(config, tab, field, column) : getFieldConfig(config, tab, field);
	return (fc?.option_values || []).some((entry) => entry?.when);
}

// Endpoint boleh berisi placeholder path `{nama}` (mis. ".../item-name/{id}"): diisi dari param bernama sama hasil
// resolveDataSourceParams, param itu tidak dikirim lagi sebagai query. `ready` false kalau ada placeholder yang
// nilainya kosong -- pemanggil jangan fetch. Endpoint tanpa `{` dikembalikan apa adanya (perilaku lama).
export function resolveEndpoint(endpoint, params) {
	const query = { ...params };
	let ready = true;
	const url = String(endpoint || '').replace(/\{(\w+)\}/g, (_, name) => {
		const value = query[name];
		delete query[name];
		if (!isFilledValue(value)) ready = false;
		return encodeURIComponent(value ?? '');
	});
	return { url, params: query, ready };
}

export function isDependencyReady(config, tab, field, ctx) {
	const deps = getFieldDependsOn(config, tab, field, ctx);
	if (deps.length === 0) return true;
	const getField = typeof ctx.getField === 'function' ? ctx.getField : () => undefined;
	return deps.every(dep => isFilledValue(getField(dep)));
}

// Versi kolom dari isDependencyReady (field repeatable): baca `depends_on` di config KOLOM. `ctx` dari
// pemanggil biasanya ctx per baris (nilai baris dulu, baru context form) -- semantik sama dengan
// FormConfigGuard::sanitizeTableRows di BE, jadi `depends_on: ["plant"]` di kolom berarti plant BARIS itu.
// Lihat FORM_CONFIG_SCHEMA.md "Scope nama field di dalam `columns`".
export function isColumnDependencyReady(config, tab, field, column, ctx) {
	const deps = getColumnConfig(config, tab, field, column)?.depends_on;
	if (!Array.isArray(deps) || deps.length === 0) return true;
	const getField = typeof ctx.getField === 'function' ? ctx.getField : () => undefined;
	return deps.every(dep => isFilledValue(getField(dep)));
}

// `dataSource.params` entries biasanya string (nama field, resolve via ctx.getField -- lihat
// FORM_CONFIG_SCHEMA.md). Entry BOLEH juga object `{ param, from, attribute }` -- generic,
// TIDAK spesifik field manapun: dipakai kalau param yang dikirim ke endpoint bukan value form
// field itu sendiri, tapi ATRIBUT dari opsi yang lagi kepilih di field lain (mis. valuation_class
// difilter oleh account_category_ref milik material_type yang dipilih, bukan oleh material_type
// code-nya). `from` = field_code yang opsinya mau dibaca, `attribute` = key di opsi itu (opsi
// harus declare attribute ini lewat `data_source.extra_fields` di field `from`), `param` =
// nama query param yang dikirim (default = `attribute` kalau tidak diisi).
export function resolveDataSourceParams(dataSource, ctx) {
	const getField = typeof ctx.getField === 'function' ? ctx.getField : () => undefined;
	const getFieldOption = typeof ctx.getFieldOption === 'function' ? ctx.getFieldOption : () => undefined;
	const params = {};
	for (const entry of dataSource?.params || []) {
		// Entry object boleh punya `when` (condition): kalau tidak cocok, param itu TIDAK dikirim (mis. filter material
		// type/group di list Item Name cuma untuk INALUM MCR002 yang bisa diedit). Tanpa `when` -> selalu dikirim (perilaku lama).
		if (entry && typeof entry === 'object' && entry.when && !evaluateCondition(entry.when, ctx)) continue;
		// `{ param, join: [nama field...], separator? }`: nilai beberapa field digabung (default "_"), mis. source ConfigDataMapping
		// "ZSPR_A001" dari cat_material_type + plant baris. Salah satu kosong -> param tidak terisi (undefined).
		if (entry && typeof entry === 'object' && Array.isArray(entry.join)) {
			const parts = entry.join.map((name) => {
				// Bagian `{ rows, where, column }`: nilai `column` dari baris PERTAMA tabel `rows` yang cocok dengan `where`
				// ({ kolom_tabel_itu: nama_field_ctx }, dibandingkan sebagai string) dan kolomnya terisi, mis. Valuation Class
				// baris Accounting yang valuation_area-nya = plant baris ini. Tidak ada baris yang cocok -> param tidak terisi.
				if (name && typeof name === 'object' && name.rows) {
					const rows = typeof ctx.getRows === 'function' ? ctx.getRows(name.rows) : [];
					const match = (Array.isArray(rows) ? rows : []).find((r) => r && isFilledValue(r[name.column])
						&& Object.entries(name.where || {}).every(([col, field]) => isFilledValue(getField(field))
							&& String(r[col] ?? '') === String(getField(field))));
					return match ? match[name.column] : undefined;
				}
				const value = getField(name);
				return value && typeof value === 'object' ? (value.value !== undefined ? value.value : value.code) : value;
			});
			params[entry.param] = parts.every((part) => isFilledValue(part)) ? parts.join(entry.separator ?? '_') : undefined;
			continue;
		}
		// `{ param, value }`: nilai tetap dari config (mis. Product Hierarchy `level` "2,3" kalau Material Type ZFGD), biasanya
		// bersama `when`.
		if (entry && typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'value')) {
			params[entry.param] = entry.value;
			continue;
		}
		// `{ param, field }`: value form field `field`, dikirim dengan nama query `param` (nama param endpoint beda
		// dengan nama field form, mis. material_group difilter `material_type` tapi field FORM02-nya `cat_material_type`).
		if (entry && typeof entry === 'object' && entry.field) {
			let value = getField(entry.field);
			if (value && typeof value === 'object') value = value.value !== undefined ? value.value : value.code;
			params[entry.param || entry.field] = value;
			continue;
		}
		if (entry && typeof entry === 'object') {
			params[entry.param || entry.attribute] = getFieldOption(entry.from, entry.attribute);
			continue;
		}
		let value = getField(entry);
		if (value && typeof value === 'object') {
			value = value.value !== undefined ? value.value : value.code;
		}
		params[entry] = value;
	}
	return params;
}

// Param bertanda `required: true` (entry object) yang kosong -> data_source ini tidak dipakai: diganti `data_source.fallback`
// (bentuk data_source biasa), atau null kalau tanpa fallback (tidak fetch). Mis. plant ANTAM dari district user
// (`user_district`), user tanpa district -> semua plant. Tanpa `required` di params -> data_source apa adanya (perilaku lama).
export function resolveDataSourceFallback(dataSource, ctx) {
	const required = (dataSource?.params || []).filter((entry) => entry && typeof entry === 'object' && entry.required);
	if (!required.length) return dataSource;
	const params = resolveDataSourceParams(dataSource, ctx);
	const missing = required.some((entry) => !isFilledValue(params[entry.param || entry.field || entry.attribute]));
	return missing ? (dataSource.fallback || null) : dataSource;
}

// ---- row_checks `compare` (field tabel) --------------------------------------------
// `{ type: "compare", field, operator, other | value, when?, message }` per BARIS (ctx baris): `field` <operator> `other` (kolom
// lain di baris) atau `value`. Gagal -> `{ column, other, message }`. Kolom `field`/`other` yang tidak tampil di baris itu ->
// pemeriksaan dilewati (mis. ANTAM tanpa Max Stock). Mirror BE `FormConfigRowChecks::compareFailures`.
const COMPARE_OPERATORS = {
	equals: (l, r) => l == r, // eslint-disable-line eqeqeq
	not_equals: (l, r) => l != r, // eslint-disable-line eqeqeq
	greater: (l, r) => l > r,
	greater_or_equal: (l, r) => l >= r,
	less: (l, r) => l < r,
	less_or_equal: (l, r) => l <= r,
};
function compareRowValues(operator, left, right) {
	const isNum = (v) => v !== null && v !== undefined && String(v).trim() !== '' && !Number.isNaN(Number(v));
	const [l, r] = isNum(left) && isNum(right) ? [Number(left), Number(right)] : [String(left ?? ''), String(right ?? '')];
	const fn = COMPARE_OPERATORS[operator] || COMPARE_OPERATORS.equals;
	return fn(l, r);
}
export function evaluateRowCompareChecks(config, tab, field, row, ctx) {
	const fc = getFieldConfig(config, tab, field);
	const rowCtx = withRowCtx(ctx, row);
	const failures = [];
	(fc?.row_checks || []).forEach((check) => {
		if (check?.type !== 'compare' || !check.field) return;
		if (check.when && !evaluateCondition(check.when, rowCtx)) return;
		const columns = [check.field, check.other].filter(Boolean);
		if (columns.some((column) => fc.columns?.[column] && !isColumnVisible(config, tab, field, column, rowCtx))) return;
		const left = rowCtx.getField(check.field);
		const right = check.other ? rowCtx.getField(check.other) : check.value;
		if (compareRowValues(check.operator || 'equals', left, right)) return;
		failures.push({ column: check.field, other: check.other || null, message: check.message || 'Invalid value' });
	});
	return failures;
}

// ---- condition evaluator -----------------------------------------------------

export function evaluateCondition(condition, ctx = {}) {
	if (!condition) return true;

	if (condition.conditions) {
		const operator = condition.operator || 'and';
		const results = condition.conditions.map(c => evaluateCondition(c, ctx));
		return operator === 'or' ? results.some(Boolean) : results.every(Boolean);
	}

	if (condition.operator === 'has_line') {
		// `where` (opsional): ada baris di section itu yang memenuhi kondisi -- dievaluasi dengan ctx BARIS (kolom baris dulu,
		// lalu form/context), mis. ada plant di luar A0N1-A0N4. Mirror FormConfigEvaluator::evaluate di BE.
		if (condition.where) {
			const rows = typeof ctx.getRows === 'function' ? ctx.getRows(condition.section) : [];
			return (Array.isArray(rows) ? rows : []).some((row) => row && typeof row === 'object' && evaluateCondition(condition.where, withRowCtx(ctx, row)));
		}
		const hasLine = typeof ctx.hasLine === 'function' ? ctx.hasLine : () => false;
		return !!hasLine(condition.section);
	}

	return evaluateLeaf(condition, ctx);
}

function evaluateLeaf(condition, ctx) {
	if (!condition.field) return false;
	const getField = typeof ctx.getField === 'function' ? ctx.getField : () => undefined;
	let value = getField(condition.field);

	if ('filled' in condition) {
		const filled = isFilledValue(value);
		return condition.filled ? filled : !filled;
	}

	if (value && typeof value === 'object') {
		value = value.value !== undefined ? value.value : (value.code !== undefined ? value.code : value);
	}

	// Nilai berupa daftar (mis. context `user_roles`): `in` cocok kalau SALAH SATU elemen ada di daftar, `not_in` kebalikannya.
	if (Array.isArray(value)) {
		if (condition.in) return value.some((v) => condition.in.includes(v));
		if (condition.not_in) return !value.some((v) => condition.not_in.includes(v));
	}
	if (condition.in) return condition.in.includes(value);
	if (condition.not_in) return !condition.not_in.includes(value);
	if ('equals' in condition) return value === condition.equals;
	if ('not_equals' in condition) return value !== condition.not_equals;
	// `starts_with`: string atau array awalan (salah satu cocok), mis. Product Hierarchy diawali "A0001". Mirror BE.
	if ('starts_with' in condition) {
		if (typeof value !== 'string' || value === '') return false;
		return [].concat(condition.starts_with).some((prefix) => typeof prefix === 'string' && prefix !== '' && value.startsWith(prefix));
	}

	return false;
}

// Ctx per BARIS field repeatable: kolom sibling di baris itu dipakai dulu (key ADA di baris, walau kosong), baru ctx
// form/context -- semantik sama dengan FormConfigGuard::sanitizeTableRows di BE (FORM_CONFIG_SCHEMA.md "Scope nama
// field di dalam `columns`"). Tanpa `row` -> ctx dikembalikan apa adanya. Dipakai engine (buildRowCtx) & New_McrFooter.
export function withRowCtx(ctx, row) {
	if (!row) return ctx;
	const getField = typeof ctx?.getField === 'function' ? ctx.getField : () => undefined;
	return { ...ctx, getField: (name) => (name in row ? row[name] : getField(name)) };
}

// Tanggal -> "YYYY-MM-DD" (waktu lokal) -- bentuk tanggal yang disimpan di item & dikirim ke BE.
export function formatDate(date) {
	const pad2 = (n) => String(n).padStart(2, '0');
	return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function isFilledValue(v) {
	if (v === null || v === undefined) return false;
	if (typeof v === 'string') return v.trim() !== '';
	if (Array.isArray(v)) return v.length > 0;
	if (typeof v === 'object') {
		const inner = v.value !== undefined ? v.value : v.code;
		return inner !== undefined ? isFilledValue(inner) : Object.keys(v).length > 0;
	}
	return true;
}

// ---- Error Save yang MASIH aktif (garis merah field/kolom & tanda "!" tab memakai aturan yang sama) ----------------
// Error ditandai validasi Save (New_McrFooter.validateSingleMcrItem): field biasa -> errors[field], kolom tabel ->
// row.rowErrors[column], jumlah baris kurang -> errors[field] berupa pesan. Error dianggap selesai begitu nilainya terisi.

// Baris tabel punya kolom bertanda error yang nilainya masih kosong (`columns` opsional: cuma kolom itu yang dicek).
export function rowHasActiveError(row, columns = null) {
	if (!row || typeof row !== 'object') return false;
	return Object.entries(row.rowErrors || {})
		.some(([column, flagged]) => flagged && (!columns || columns.includes(column)) && !isFilledValue(row[column]));
}

// Tab punya error Save yang masih aktif untuk item ini. `errors` = error item itu (setErrorValidCat[index]).
export function hasActiveTabError(config, tab, item, errors, ctx) {
	if (!item) return false;
	const errs = errors || {};
	return Object.entries(getTabFieldMap(config, tab) || {}).some(([field, fc]) => {
		if (!fc || typeof fc !== 'object' || fc.display_only) return false;
		if (fc.columns || fc.row_rules) {
			const rows = Array.isArray(item[field]) ? item[field] : [];
			if (rows.some((row) => rowHasActiveError(row))) return true;
			// Pesan jumlah baris kurang: aktif selama baris terisi masih di bawah minimum.
			if (typeof errs[field] !== 'string') return false;
			const columns = Object.keys(fc.columns || {});
			const filledRows = columns.length
				? rows.filter((row) => columns.some((column) => isFilledValue(row?.[column]))).length
				: rows.length;
			return filledRows < getRequiredRowCount(config, tab, field, ctx);
		}
		return !!errs[field] && !isFilledValue(item[field]);
	});
}

export default {
	normalizeFormConfig,
	getTabFieldMap,
	getFieldConfig,
	isTabVisible, // untuk tab dan section
	isFieldVisible,
	isFieldEnabled,
	isFieldMandatory, // untuk kebutuhan nampilin * pada 1 field
	isSectionMandatory, // untuk kebutuhan nampilin * pada header/tab
	getColumnConfig,
	isColumnVisible,
	isColumnEnabled,
	isColumnMandatory,
	isRowActionAllowed,
	getUniqueColumnPeers,
	isReadonlySaved,
	isColumnReadonlySaved,
	isFieldMultiple,
	applyLockedDefaults,
	applyValuePrefixes,
	evaluateRowCompareChecks,
	resolveSimpleDefault,
	getConditionFields,
	getRowRuleValue,
	getRequiredRowCount,
	hasVisibleColumn,
	resolveActiveDefault,
	resolveOptionValues,
	resolveOptionRule,
	isOptionValueAllowed,
	hasConditionalOptionValues,
	resolveActiveColumnDefault,
	resolveActiveHydrate,
	resolveDependsOn,
	getFieldDependsOn,
	isAutoSelectSingle,
	resolveEndpoint,
	getFieldDataSource,
	isDependencyReady,
	isColumnDependencyReady,
	resolveDataSourceParams,
	resolveDataSourceFallback,
	evaluateCondition,
	withRowCtx,
	formatDate,
	isFilledValue,
	rowHasActiveError,
	hasActiveTabError,
};